import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import {
  SUBPROCESSORS,
  SUBPROCESSORS_LAST_REVIEWED,
  ROLE_LABELS,
  type SubprocessorRole,
} from '../config/subprocessors.generated';

/**
 * The Trust Center (2026-09-11).
 *
 * Prest0 sells to law firms handling privileged client data, and had no
 * subprocessor list at all — around twenty outside parties receive customer
 * content and none was named anywhere. CalOPPA §22575(b) and CCPA §1798.100(b)
 * ask for the third parties; a buyer's security questionnaire asks for their
 * names, in writing, before signing.
 *
 * So this page is not a compliance interruption. It is the page a prospect
 * goes looking for, and it exists to shorten the questionnaire — which is why
 * it is written in plain language and states the awkward parts (where a vendor
 * is outside the US, what the SOC 2 posture really covers, that self-service
 * deletion is not built yet) rather than burying them. A trust page that
 * flatters is worth nothing to the reader who has to sign off on it.
 *
 * The list itself is GENERATED from config/subprocessors.json and gated by
 * scripts/check-subprocessors-sync.mjs against the vendors the code can
 * actually reach, so it cannot quietly go stale.
 */

const Page = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 4rem 2rem 6rem;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #333;

  @media (max-width: 768px) {
    padding: 3rem 1.25rem 4rem;
  }
`;

const Header = styled.header`
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 600;
  margin: 0 0 1rem;

  @media (max-width: 768px) {
    font-size: 2.25rem;
  }
`;

const Lede = styled.p`
  font-size: 1.2rem;
  line-height: 1.65;
  color: #555;
  max-width: 44rem;
  margin: 0 0 0.75rem;
`;

const Section = styled.section`
  margin-top: 3.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 600;
  margin: 0 0 0.75rem;
`;

const Body = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: #555;
  max-width: 44rem;
  margin: 0 0 1rem;
`;

const Points = styled.ul`
  margin: 0 0 1rem;
  padding-left: 1.15rem;
  max-width: 44rem;

  li {
    font-size: 1rem;
    line-height: 1.7;
    color: #555;
    margin-bottom: 0.5rem;
  }
`;

const GroupTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--brand-charcoal);
  margin: 2rem 0 0.85rem;
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(300px, 100%), 1fr));
  gap: 1rem;
`;

const Card = styled.div`
  border: 1px solid #e8e4d9;
  border-radius: 0.9rem;
  padding: 1.1rem 1.2rem;
  background: #fcfbf7;
`;

const VendorName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: var(--brand-charcoal);
  margin-bottom: 0.35rem;
`;

const VendorPurpose = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  color: #5a5a5a;
  margin: 0 0 0.7rem;
`;

const Meta = styled.dl`
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.55;
  color: #6b6b6b;

  div {
    display: flex;
    gap: 0.4rem;
  }

  dt {
    font-weight: 600;
    color: #555;
    flex: 0 0 auto;
  }

  dd {
    margin: 0;
  }
`;

const VendorLink = styled.a`
  display: inline-block;
  margin-top: 0.6rem;
  font-size: 0.82rem;
  color: #6b6b6b;
  border-bottom: 1px solid rgba(198, 167, 94, 0.5);
  text-decoration: none;

  &:hover { color: var(--brand-charcoal); }
`;

const Reviewed = styled.p`
  font-size: 0.88rem;
  color: #777;
  margin: 1.25rem 0 0;
`;

const Contact = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: #555;

  a {
    color: var(--brand-gold);
    text-decoration: none;
    border-bottom: 1px solid rgba(198, 167, 94, 0.5);
  }
`;

const Related = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1.25rem;

  a {
    font-size: 0.95rem;
    color: #555;
    text-decoration: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);

    &:hover { color: var(--brand-charcoal); }
  }
`;

/** How the vendor is reached, in words a reader does not have to decode. */
const REACH_LABEL: Record<string, string> = {
  'direct': 'called directly by prest0',
  'via-openrouter': 'reached through the model gateway',
  'both': 'both directly and through the gateway',
};

const ROLE_ORDER: SubprocessorRole[] = [
  'infrastructure', 'model-gateway', 'model-provider', 'media-provider',
  'connections', 'payments', 'communications', 'operations',
];

const TrustPage: React.FC = () => {
  const grouped = ROLE_ORDER
    .map((role) => ({ role, rows: SUBPROCESSORS.filter((s) => s.role === role) }))
    .filter((g) => g.rows.length > 0);

  return (
    <Page>
      <SeoHead
        title="Trust Center | prest0"
        description="Who processes your data, where it goes, and what we do and do not do with it. The full subprocessor list, kept in step with the running system."
        canonicalUrl="https://prest0.ai/trust"
      />

      <Header>
        <Title>trust center</Title>
        <Lede>
          who touches your data, where it goes, and what we do and don&rsquo;t do with it.
          written to be read before you sign something, so it says the awkward parts too.
        </Lede>
        <Reviewed>
          last reviewed against the running system on {SUBPROCESSORS_LAST_REVIEWED}.
        </Reviewed>
      </Header>

      <Section>
        <SectionTitle>the short version</SectionTitle>
        <Points>
          <li>
            your prompts, transcripts and files are never used to train shared models,
            and we don&rsquo;t sell or rent personal information.
          </li>
          <li>
            we run no analytics, no advertising pixels and no third-party trackers on
            this site — which is why you&rsquo;ve never seen a cookie wall here.
          </li>
          <li>
            we do not run a redaction layer over your prompts. what you send is what the
            model sees. we say so plainly because the opposite claim is easy to make and
            hard to check.
          </li>
          <li>
            self-service export and deletion are being built. today a deletion request is
            an operational process — email us and we do it, across the database, stored
            files and transcripts, and confirm when it&rsquo;s done.
          </li>
        </Points>
      </Section>

      <Section>
        <SectionTitle>who processes your data</SectionTitle>
        <Body>
          every company below can receive something on prest0&rsquo;s behalf. the list is
          generated from the same file the build checks against the running code, so a new
          vendor cannot ship without appearing here — that check is the reason this page
          can be trusted rather than merely published.
        </Body>
        <Body>
          most model requests travel through a gateway (openrouter) to whichever model the
          task calls for, so the model companies below receive prompt content even where
          we hold no account with them directly. a few are outside the united states; those
          say so.
        </Body>

        {grouped.map(({ role, rows }) => (
          <div key={role}>
            <GroupTitle>{ROLE_LABELS[role]}</GroupTitle>
            <Cards>
              {rows.map((s) => (
                <Card key={s.id}>
                  <VendorName>{s.name}</VendorName>
                  <VendorPurpose>{s.purpose}</VendorPurpose>
                  <Meta>
                    <div>
                      <dt>your data:</dt>
                      <dd>{s.customerData ? s.dataCategories.join(', ') : 'none — internal use only'}</dd>
                    </div>
                    <div>
                      <dt>region:</dt>
                      <dd>{s.region}</dd>
                    </div>
                    <div>
                      <dt>reached:</dt>
                      <dd>{REACH_LABEL[s.reach] ?? s.reach}</dd>
                    </div>
                  </Meta>
                  <VendorLink href={s.url} target="_blank" rel="noopener noreferrer">
                    their privacy terms
                  </VendorLink>
                </Card>
              ))}
            </Cards>
          </div>
        ))}

        <Reviewed>
          if you want to be told when this list changes before it changes, email us and
          we&rsquo;ll add you to the notice list.
        </Reviewed>
      </Section>

      <Section>
        <SectionTitle>the applications you connect</SectionTitle>
        <Body>
          when you link an application to your workspace — mail, calendar, a crm — that
          application receives data because you asked for the connection. those are your
          vendors rather than ours, so they aren&rsquo;t on the list above; you can see and
          disconnect every one of them on your integrations page. the broker that holds
          those connections, composio, is on the list, because we chose it.
        </Body>
      </Section>

      <Section>
        <SectionTitle>where prest0 runs</SectionTitle>
        <Body>
          the platform runs on google cloud in the united states. the soc 2 type ii and
          iso 27001 certifications behind it are google cloud&rsquo;s, covering the
          infrastructure prest0 is built on — prest0 itself does not hold its own soc 2
          report, and we&rsquo;d rather tell you that here than let a badge imply otherwise.
        </Body>
      </Section>

      <Section>
        <SectionTitle>asking us something</SectionTitle>
        <Contact>
          security questionnaire, data processing agreement, subprocessor change notices,
          or a deletion or export request:{' '}
          <a href="mailto:support@prest0.ai">support@prest0.ai</a>. we answer verified
          deletion requests within 30 days.
        </Contact>
        <Related>
          <Link to="/privacy-policy">privacy policy</Link>
          <Link to="/terms-of-service">terms of service</Link>
          <Link to="/cookie-policy">cookie policy</Link>
          <Link to="/security">security</Link>
        </Related>
      </Section>
    </Page>
  );
};

export default TrustPage;
